<script setup>
// Caixas de Entrada: painel de SAUDE dos canais, nao so um cadastro.
//
// A pagina e visitada de vez em quando para criar caixa e com urgencia quando
// algo cai — o layout serve a urgencia. Tres decisoes que vem disso:
// - a barra de cima responde "esta tudo de pe?" antes de qualquer lista;
// - agrupa por CANAL, nao por alfabeto: WhatsApp Web (sessao de QR) e WhatsApp
//   Oficial (Cloud API) quebram e reconectam de formas diferentes, e antes a
//   unica pista era uma palavra em cinza de 12px;
// - caixa sem conversa ha 30 dias vira "parada" e sai do amarelo de urgencia.
//   Enquanto morta e quebrada disputavam a mesma cor, o vermelho nao valia nada.
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useRouter } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import { usePermissions } from 'dashboard/composables/usePermissions';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();
// Escrita em inboxes: canManageInboxes cobre admin + settings_manage_inboxes + inbox_manage.
const { canManageInboxes } = usePermissions();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const activeFilter = ref('all');
const searchQuery = ref('');
const health = ref({});

// Menu dos tres pontinhos: vai por Teleport + position fixed porque o card do
// grupo tem overflow-hidden — o menu absoluto era fatiado na ultima caixa de
// cada grupo, e sobrava so a primeira opcao.
const menu = ref({ inboxId: null, x: 0, y: 0 });
const menuRef = ref(null);

const fecharMenu = () => {
  menu.value = { inboxId: null, x: 0, y: 0 };
};

const alternarMenu = async (evento, inbox) => {
  if (menu.value.inboxId === inbox.id) {
    fecharMenu();
    return;
  }

  const alvo = evento.currentTarget.getBoundingClientRect();
  menu.value = { inboxId: inbox.id, x: alvo.right, y: alvo.bottom + 6 };

  // Mede o menu ja renderizado pra decidir se cabe embaixo; sem isso a
  // ultima caixa da pagina abria o menu pra fora da janela.
  await nextTick();
  const el = menuRef.value;
  if (!el) return;

  const { width, height } = el.getBoundingClientRect();
  const margem = 8;
  const x = Math.max(
    margem,
    Math.min(alvo.right - width, window.innerWidth - width - margem)
  );
  const cabeEmbaixo = alvo.bottom + 6 + height <= window.innerHeight - margem;
  menu.value = {
    inboxId: inbox.id,
    x,
    y: cabeEmbaixo ? alvo.bottom + 6 : Math.max(margem, alvo.top - height - 6),
  };
};

// Rolar ou redimensionar com o menu aberto deixaria ele solto no meio da tela.
onMounted(() => {
  window.addEventListener('scroll', fecharMenu, true);
  window.addEventListener('resize', fecharMenu);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', fecharMenu, true);
  window.removeEventListener('resize', fecharMenu);
});

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const th = key => t(`INBOX_MGMT.HEALTH.${key}`);

// Metricas vem de /inboxes/overview: o payload de inbox e carregado em toda
// navegacao do app e nao deve carregar contagem de conversa por causa desta tela.
const fetchHealth = async () => {
  try {
    const { data } = await InboxesAPI.getOverview();
    health.value = (data.payload || []).reduce((acc, row) => {
      acc[row.id] = row;
      return acc;
    }, {});
  } catch {
    health.value = {};
  }
};
onMounted(fetchHealth);

const DORMANT_DAYS = 30;

const diasDesde = iso => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

const canConfigureInbox = inbox =>
  canManageInboxes.value || inbox.current_user_inbox_role === 'admin';

// Estado operacional (3) no lugar dos estados tecnicos (6): e o que decide se
// alguem precisa agir agora.
const decorate = inbox => {
  const metrics = health.value[inbox.id] || {};
  const idleDays = diasDesde(metrics.last_activity_at);
  const status = inbox.connection_status;
  const quebrada = ['disconnected', 'needs_reauth', 'qr_pending'].includes(
    status
  );
  const parada = quebrada && (idleDays === null || idleDays >= DORMANT_DAYS);

  let acao = null;
  let motivo = '';
  if (status === 'qr_pending') {
    acao = { label: th('SEE_QR'), urgent: false };
    motivo = th('WHY_QR');
  } else if (status === 'needs_reauth') {
    acao = { label: th('REAUTHORIZE'), urgent: true };
    motivo = th('WHY_REAUTH');
  } else if (status === 'disconnected') {
    acao = { label: th('RECONNECT'), urgent: true };
    motivo = th('WHY_DISCONNECTED');
  }

  let bucket = 'ok';
  if (parada) bucket = 'dormant';
  else if (quebrada || status === 'connecting') bucket = 'attention';

  return {
    ...inbox,
    metrics,
    idleDays,
    bucket,
    acao,
    motivo,
    // Caixa recebendo sem ninguem para atender e um problema silencioso que a
    // tela antiga nao mostrava em lugar nenhum.
    orfa: (metrics.conversations_7d || 0) > 0 && metrics.members_count === 0,
  };
};

const visibleInboxes = computed(() => {
  const base = canManageInboxes.value
    ? inboxes.value || []
    : (inboxes.value || []).filter(i => i.current_user_inbox_role === 'admin');
  return base.map(decorate);
});

const resumo = computed(() => {
  const list = visibleInboxes.value;
  return {
    total: list.length,
    ok: list.filter(i => i.bucket === 'ok').length,
    attention: list.filter(i => i.bucket === 'attention').length,
    dormant: list.filter(i => i.bucket === 'dormant').length,
  };
});

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return visibleInboxes.value.filter(inbox => {
    if (activeFilter.value !== 'all' && inbox.bucket !== activeFilter.value) {
      return false;
    }
    if (!q) return true;
    return `${inbox.name} ${inbox.metrics.phone_number || ''}`
      .toLowerCase()
      .includes(q);
  });
});

// Ordem dentro do grupo: quem precisa de acao primeiro, depois por volume —
// alfabetico deixava a caixa morta em cima e a que fatura no fim.
const PESO = { attention: 0, ok: 1, dormant: 2 };
const grupos = computed(() => {
  const mapa = new Map();
  filtered.value.forEach(inbox => {
    if (!mapa.has(inbox.channel_type)) mapa.set(inbox.channel_type, []);
    mapa.get(inbox.channel_type).push(inbox);
  });
  return [...mapa.entries()]
    .map(([channelType, items]) => ({
      channelType,
      hint: t(`INBOX_MGMT.HEALTH.GROUP_HINT.${channelType}`, ''),
      items: items.sort(
        (a, b) =>
          PESO[a.bucket] - PESO[b.bucket] ||
          (b.metrics.conversations_7d || 0) - (a.metrics.conversations_7d || 0)
      ),
    }))
    .sort((a, b) => b.items.length - a.items.length);
});

const ultimaAtividade = inbox => {
  const dias = inbox.idleDays;
  if (dias === null) return th('NEVER');
  if (dias === 0) return th('TODAY');
  if (dias === 1) return th('YESTERDAY');
  return new Date(inbox.metrics.last_activity_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
};

const abrirInbox = inbox => {
  router.push({ name: 'settings_inbox_show', params: { inboxId: inbox.id } });
};

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);
const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);
// A caixa do menu aberto (o menu vive fora da lista, entao precisa saber quem e).
const menuAberto = computed(() =>
  inboxes.value.find(inbox => inbox.id === menu.value.inboxId)
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(() =>
  t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
    inboxName: selectedInbox.value.name,
  })
);

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = async () => {
  const { id } = selectedInbox.value;
  closeDelete();
  try {
    await store.dispatch('inboxes/delete', id);
    toast.success(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    fetchHealth();
  } catch (error) {
    toast.error(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDelete = inbox => {
  fecharMenu();
  selectedInbox.value = inbox;
  showDeletePopup.value = true;
};
</script>

<template>
  <SettingsPageLayout
    :title="$t('INBOX_MGMT.HEADER')"
    :description="$t('INBOX_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <router-link v-if="canManageInboxes" :to="{ name: 'settings_inbox_new' }">
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
        />
      </router-link>
    </template>

    <!-- Saúde: responde "está tudo de pé?" antes da lista -->
    <div
      class="flex items-center gap-5 flex-wrap rounded-xl bg-n-solid-2 outline outline-1 outline-n-container px-4 py-3 mb-5"
    >
      <span class="inline-flex items-baseline gap-1.5">
        <b class="font-headline text-lg font-bold tabular-nums text-n-slate-12">
          {{ resumo.total }}
        </b>
        <span class="text-xs text-n-slate-10">{{ th('INBOXES') }}</span>
      </span>
      <span class="w-px h-6 bg-n-weak" />
      <span class="inline-flex items-baseline gap-1.5">
        <b class="font-headline text-lg font-bold tabular-nums text-n-teal-11">
          {{ resumo.ok }}
        </b>
        <span class="text-xs text-n-slate-10">{{ th('CONNECTED') }}</span>
      </span>
      <span v-if="resumo.attention" class="inline-flex items-baseline gap-1.5">
        <b class="font-headline text-lg font-bold tabular-nums text-n-ruby-11">
          {{ resumo.attention }}
        </b>
        <span class="text-xs text-n-slate-10">{{ th('NEEDS_YOU') }}</span>
      </span>
      <span v-if="resumo.dormant" class="inline-flex items-baseline gap-1.5">
        <b class="font-headline text-lg font-bold tabular-nums text-n-slate-10">
          {{ resumo.dormant }}
        </b>
        <span class="text-xs text-n-slate-10">{{ th('DORMANT') }}</span>
      </span>

      <div class="flex gap-1.5 flex-wrap ml-auto items-center">
        <button
          v-for="f in ['all', 'attention', 'dormant']"
          :key="f"
          class="rounded-full px-3 py-1 text-xs border transition-colors"
          :class="
            activeFilter === f
              ? 'bg-n-slate-12 border-n-slate-12 text-n-solid-1 font-medium'
              : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:bg-n-slate-2'
          "
          @click="activeFilter = f"
        >
          {{
            f === 'all'
              ? th('ALL')
              : f === 'attention'
                ? th('ATTENTION')
                : th('DORMANT_FILTER')
          }}
        </button>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="th('SEARCH')"
          class="!mb-0 !h-7 w-40 rounded-full text-xs"
        />
      </div>
    </div>

    <div v-if="uiFlags.isFetching" class="py-10 text-center text-n-slate-10">
      <span class="i-lucide-loader-circle size-5 animate-spin inline-block" />
    </div>

    <div
      v-else-if="!filtered.length"
      class="py-12 text-center text-sm text-n-slate-10"
    >
      {{
        visibleInboxes.length ? th('EMPTY_FILTER') : $t('INBOX_MGMT.LIST.404')
      }}
    </div>

    <div v-for="grupo in grupos" :key="grupo.channelType" class="mb-6">
      <div class="flex items-baseline gap-2.5 flex-wrap mx-1 mb-2">
        <span
          class="text-[11px] font-bold uppercase tracking-[0.07em] text-n-slate-11"
        >
          <ChannelName :channel-type="grupo.channelType" />
        </span>
        <span class="text-[11px] tabular-nums text-n-slate-9">
          {{ `${grupo.items.length} ${th('INBOXES')}` }}
        </span>
        <span v-if="grupo.hint" class="ml-auto text-[11.5px] text-n-slate-9">
          {{ grupo.hint }}
        </span>
      </div>

      <div
        class="rounded-xl bg-n-solid-1 outline outline-1 outline-n-container overflow-hidden"
      >
        <div
          v-for="inbox in grupo.items"
          :key="inbox.id"
          class="relative flex items-center gap-3.5 px-4 py-3 border-b border-n-weak last:border-b-0 transition-colors hover:bg-n-solid-2"
          :class="{ 'bg-n-solid-2/40': inbox.bucket === 'dormant' }"
        >
          <div class="relative flex-none">
            <div
              class="size-10 flex justify-center items-center bg-n-alpha-3 rounded-full p-1.5 ring ring-n-solid-1 border border-n-strong shadow-sm"
            >
              <Avatar
                v-if="inbox.avatar_url"
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="28"
                rounded-full
              />
              <ChannelIcon
                v-else
                class="size-4 text-n-slate-10"
                :inbox="inbox"
              />
            </div>
            <span
              v-if="inbox.connection_status"
              class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-n-solid-1"
              :class="{
                'bg-n-teal-9': inbox.bucket === 'ok',
                'bg-n-ruby-9': inbox.bucket === 'attention',
                'bg-n-slate-8': inbox.bucket === 'dormant',
              }"
            />
          </div>

          <button
            class="flex-1 min-w-0 text-left"
            :class="{ 'opacity-60': inbox.bucket === 'dormant' }"
            @click="abrirInbox(inbox)"
          >
            <span class="block text-sm font-medium text-n-slate-12 truncate">
              {{ inbox.name }}
            </span>
            <span class="flex items-center gap-2 flex-wrap mt-0.5">
              <span
                v-if="inbox.metrics.phone_number"
                class="text-xs tabular-nums text-n-slate-10"
              >
                {{ inbox.metrics.phone_number }}
              </span>
              <span
                v-for="outra in inbox.metrics.shared_with || []"
                :key="outra.id"
                class="text-[11px] rounded px-1.5 py-px bg-n-amber-3 text-n-amber-11"
              >
                {{ th('SHARED').replace('{name}', outra.name) }}
              </span>
              <span
                v-if="inbox.orfa"
                class="text-[11px] rounded px-1.5 py-px bg-n-ruby-3 text-n-ruby-11"
              >
                {{
                  th('NO_AGENTS').replace(
                    '{count}',
                    inbox.metrics.conversations_7d
                  )
                }}
              </span>
            </span>
          </button>

          <div
            class="hidden md:flex gap-5 flex-none mr-1"
            :class="{ 'opacity-60': inbox.bucket === 'dormant' }"
          >
            <span class="text-right min-w-[64px]">
              <b
                class="block font-headline text-sm font-bold tabular-nums text-n-slate-12"
              >
                {{ inbox.metrics.conversations_7d ?? '—' }}
              </b>
              <span class="text-[10.5px] text-n-slate-9">
                {{ th('CONVERSATIONS') }}
              </span>
            </span>
            <span class="text-right min-w-[48px]">
              <b
                class="block font-headline text-sm font-bold tabular-nums"
                :class="inbox.orfa ? 'text-n-ruby-11' : 'text-n-slate-12'"
              >
                {{ inbox.metrics.members_count ?? '—' }}
              </b>
              <span class="text-[10.5px] text-n-slate-9">{{
                th('AGENTS')
              }}</span>
            </span>
            <span class="text-right min-w-[48px]">
              <b
                class="block font-headline text-sm font-bold tabular-nums text-n-slate-12"
              >
                {{ ultimaAtividade(inbox) }}
              </b>
              <span class="text-[10.5px] text-n-slate-9">{{ th('LAST') }}</span>
            </span>
          </div>

          <div class="flex-none min-w-[168px] text-right">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11.5px] font-semibold rounded-full"
              :class="{
                'bg-n-teal-3 text-n-teal-11': inbox.bucket === 'ok',
                'bg-n-ruby-3 text-n-ruby-11': inbox.bucket === 'attention',
                'bg-n-slate-3 text-n-slate-10': inbox.bucket === 'dormant',
              }"
            >
              {{
                inbox.bucket === 'ok'
                  ? th('CONNECTED')
                  : inbox.bucket === 'dormant'
                    ? th('IDLE_FOR').replace('{days}', inbox.idleDays ?? 0)
                    : th('NEEDS_YOU')
              }}
            </span>
            <span
              v-if="inbox.motivo"
              class="block text-[11px] text-n-slate-10 mt-1 leading-snug"
            >
              {{ inbox.motivo }}
            </span>
            <Button
              v-if="inbox.acao && canConfigureInbox(inbox)"
              class="mt-1.5"
              xs
              :faded="!inbox.acao.urgent"
              :slate="!inbox.acao.urgent"
              :ruby="inbox.acao.urgent"
              :label="inbox.acao.label"
              @click="abrirInbox(inbox)"
            />
          </div>

          <Button
            v-if="canConfigureInbox(inbox)"
            icon="i-lucide-ellipsis"
            slate
            xs
            ghost
            @click="alternarMenu($event, inbox)"
          />
        </div>
      </div>
    </div>

    <!-- eslint-disable-next-line vue/no-static-inline-styles -->
    <Teleport to="body">
      <template v-if="menuAberto">
        <!-- Camada que fecha ao clicar em qualquer lugar, o proprio botao incluso -->
        <div class="fixed inset-0 z-[70]" @click="fecharMenu" />
        <div
          ref="menuRef"
          class="fixed z-[71] min-w-[188px] rounded-xl bg-n-solid-1 outline outline-1 outline-n-container shadow-lg p-1.5"
          :style="{ left: `${menu.x}px`, top: `${menu.y}px` }"
        >
          <button
            class="flex items-center gap-2.5 w-full text-left px-2.5 py-1.5 rounded-lg text-xs text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12"
            @click="abrirInbox(menuAberto)"
          >
            <span class="i-lucide-settings size-3.5" />
            {{ th('MENU_SETTINGS') }}
          </button>
          <button
            class="flex items-center gap-2.5 w-full text-left px-2.5 py-1.5 rounded-lg text-xs text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12"
            @click="abrirInbox(menuAberto)"
          >
            <span class="i-lucide-users size-3.5" />
            {{ th('MENU_MEMBERS') }}
          </button>
          <template v-if="canManageInboxes">
            <hr class="border-0 border-t border-n-weak my-1.5 mx-0.5" />
            <button
              class="flex items-center gap-2.5 w-full text-left px-2.5 py-1.5 rounded-lg text-xs text-n-ruby-11 hover:bg-n-ruby-3"
              @click="openDelete(menuAberto)"
            >
              <span class="i-lucide-trash-2 size-3.5" />
              {{ th('MENU_DELETE') }}
            </button>
          </template>
        </div>
      </template>
    </Teleport>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsPageLayout>
</template>
